/**
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * Vercel deployment variables
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * */
export const IS_VERCEL_DEPLOYMENT = process.env?.VERCEL === '1';

export const IS_VERCEL_PROD =
  IS_VERCEL_DEPLOYMENT && process.env.VERCEL_ENV === 'production';

export const IS_VERCEL_PREVIEW =
  IS_VERCEL_DEPLOYMENT && process.env.VERCEL_ENV === 'preview';

export const IS_VERCEL_DEV =
  IS_VERCEL_DEPLOYMENT && process.env.VERCEL_ENV === 'development';

export const VERCEL_TEAM = process.env.VERCEL_TEAM!;

/**
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * API environment variables
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * */

export const API_URL = process.env.API_URL || 'http://localhost:8080/api';
export const IS_PROD_API =
  API_URL.includes('api.nominal.so') ||
  API_URL.includes('api-global.nominal.so');

export const IS_STG_API =
  API_URL.includes('api.stg.nominal.so') ||
  API_URL.includes('api-global.stg.nominal.so');

export const IS_DEV_API = API_URL.includes('api.dev.nominal.so');

export const { EXPORTER_URL } = process.env;

/**
 * +++++++++++++++++++++++++++++++++++++a++++++++++++++++++++++++++++++++++++++++
 * System environment variables
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * */

export const IS_PROD = IS_PROD_API && IS_VERCEL_PROD;

export const IS_PROD_PREVIEW = IS_PROD_API && IS_VERCEL_PREVIEW;

export const IS_PROD_DEV = IS_DEV_API && IS_VERCEL_DEV;

export const IS_STG = IS_STG_API && IS_VERCEL_PROD;

export const IS_STG_PREVIEW = IS_STG_API && IS_VERCEL_PREVIEW;

export const IS_STG_DEV = IS_DEV_API && IS_VERCEL_DEV;

/**
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * Other environment variables
 * +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * */

/** Try to determine from which domain UI will be served based on the deployment environment. */
export const UI_DOMAIN = (() => {
  if (IS_VERCEL_PROD) {
    return `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`;
  }

  if (IS_VERCEL_PREVIEW) {
    if ((process.env.VERCEL_BRANCH_URL || '').length > 0) {
      return `https://${process.env.VERCEL_BRANCH_URL}`;
    }

    return `https://${process.env.VERCEL_URL}`;
  }

  // In case we are on dev-env, we use domain alias for UI
  // and these are not populated in VERCEL_URL variables
  if (IS_DEV_API && process.env.UI_URL) {
    return process.env.UI_URL;
  }

  return 'http://localhost:3000';
})();

/**
 * Whether Set-Cookie should include `Secure`. Must be false on local HTTP
 * (`http://localhost:3000`) and true for HTTPS deployments (Vercel / preview).
 */
export const USE_SECURE_COOKIES =
  typeof UI_DOMAIN === 'string' && UI_DOMAIN.startsWith('https://');

/**
 * Feature flag selecting how the Descope session cookies (DS/DSR) are scoped:
 * `true` → domain cookie (see `COOKIE_DOMAIN`), `false` → host-only cookie. Off
 * by default so the existing host-cookie behaviour is preserved; set the
 * `USE_COOKIE_DOMAIN` env var on a deployment to opt into domain cookies.
 */
export const USE_COOKIE_DOMAIN: boolean = !!process.env.USE_COOKIE_DOMAIN;

/**
 * `domain` attribute for the Descope session cookies (DS/DSR), or `undefined`
 * for host-only cookies. When enabled via `USE_COOKIE_DOMAIN` on the stable
 * stg/prod deployments, it is the custom production host
 * (`VERCEL_PROJECT_PRODUCTION_URL` → `app.nominal.so` / `app.stg.nominal.so`).
 * Setting the domain explicitly turns these into domain cookies, so the browser
 * also sends them to sibling subdomains such as the Descope custom auth domain
 * (`auth.app.nominal.so`) — which is what lets an embedded app authenticate
 * silently against Descope. Left `undefined` when the flag is off, or on dev /
 * preview hosts (dynamic / per-namespace), so the cookies stay host-only there.
 */
export const COOKIE_DOMAIN: string | undefined = (() => {
  if (USE_COOKIE_DOMAIN && (IS_PROD || IS_STG)) {
    return process.env.VERCEL_PROJECT_PRODUCTION_URL;
  }

  return undefined;
})();

export const USE_METICULOUS = IS_STG || IS_STG_PREVIEW;

export const USE_POSTHOG = !!(
  process.env.POSTHOG_KEY &&
  (IS_PROD_API || process.env?.USE_POSTHOG)
);

export const USE_STATUS_WIDGET = IS_PROD_API;

// AI Studio base URL, embedded on the Flows page. Server-only; unset → grid.
export const AI_STUDIO_URL = process.env.NOMI_AI_STUDIO;

export const USE_SENTRY = !!(
  process.env.NEXT_PUBLIC_SENTRY_DSN && process.env.NEXT_PUBLIC_USE_SENTRY
);

export const VERCEL_PROJECT = (() => {
  if (IS_VERCEL_DEPLOYMENT) {
    if (IS_PROD_API) {
      return 'prod-eu-central-1';
    }

    if (IS_STG_API) {
      return 'stg-eu-central-1';
    }
  }

  return 'dev';
})();

export const EDGE_CONFIG_ID = (() => {
  if (process.env.EDGE_CONFIG) {
    const url = new URL(process.env.EDGE_CONFIG!);
    const pathParts = url.pathname.split('/');
    return pathParts[pathParts.length - 1];
  }

  return undefined;
})();

export const EXPORT_ROW_LIMIT = process.env.NEXT_PUBLIC_EXPORT_ROW_LIMIT
  ? parseInt(process.env.NEXT_PUBLIC_EXPORT_ROW_LIMIT)
  : 1000;

/**
 * Minimum including whole calendar days since last bank sync before the Bank Feed column in Bank reconciliation
 * shows the stale (red) styling. Set via `NEXT_PUBLIC_BANK_FEED_SYNC_STALE_MIN_DAYS`
 * (integer); fall back to 2.
 */
export const BANK_FEED_SYNC_STALE_MIN_DAYS = process.env
  .NEXT_PUBLIC_BANK_FEED_SYNC_STALE_MIN_DAYS
  ? parseInt(process.env.NEXT_PUBLIC_BANK_FEED_SYNC_STALE_MIN_DAYS)
  : 2;

export const HAS_LIVEBLOCKS_ENV =
  process.env?.LIVEBLOCKS_SECRET_KEY && process.env?.LIVEBLOCKS_WEBHOOK_SECRET;

export const SUPPORT_USER_IDS = {
  DEV: 'U2eZwMJgo8tpFpsVEVKPdrOjNx9C',
  STG: 'U2dXQ70PxEIEe4tnWFea8JIRORfD',
  PROD: 'U2eJHmihzEiTrGVPkRqIMQvYvLN0',
};

export const SUPPORT_USER_IDS_LIST = Object.values(SUPPORT_USER_IDS);

export const SUPPORT_USER = (() => {
  let id = SUPPORT_USER_IDS.DEV;

  if (IS_PROD_API) {
    id = SUPPORT_USER_IDS.PROD;
  }

  if (IS_STG_API) {
    id = SUPPORT_USER_IDS.STG;
  }

  return {
    id,
    email: 'support+agent@nominal.so',
    name: 'Nominal Support',
    picture: null,
  };
})();

export const STATUS_PAGE_URL = 'https://status.nominal.so';

/**
 * Deployment-level flag for the demo environment, set via `NEXT_PUBLIC_IS_DEMO`
 * on the demo deployment (it serves the production API, so `IS_PROD_API` alone
 * cannot distinguish it). Demo deployment behavior — `tenantAccessGuard` hiding
 * every tenant not in `ALLOWED_TENANTS` — keys off this.
 */
export const IS_DEMO_ENV = !!process.env.NEXT_PUBLIC_IS_DEMO;

/**
 * The demo tenants. The comma-separated `ALLOWED_TENANTS` env variable supplies
 * the list, set on every deployment that has demo tenants (the prod app and the
 * demo deployment). `isDemoTenant` turns on per-tenant demo features for them on
 * any deployment; the demo deployment additionally hides every tenant not on the
 * list (`tenantAccessGuard`, gated on `IS_DEMO_ENV`). The variable is not
 * `NEXT_PUBLIC`, so client bundles see `[]`.
 */
export const ALLOWED_TENANTS: string[] = (process.env.ALLOWED_TENANTS ?? '')
  .split(',')
  .map((name) => name.trim())
  .filter(Boolean);

/**
 * A demo tenant gets the per-tenant demo features (data reset, Liveblocks
 * support user) on any deployment it lives on — membership alone decides.
 * Deployment-level demo behavior keys off `IS_DEMO_ENV` instead.
 */
export const isDemoTenant = (tenant: string): boolean =>
  ALLOWED_TENANTS.includes(tenant);

/** 🔒 Allowed domains for redirect validation (built from Vercel env variables) */
export const ALLOWED_REDIRECT_DOMAINS: string[] = (() => {
  const domains = new Set<string>();

  if (process.env.VERCEL_PROJECT_PRODUCTION_URL) {
    domains.add(process.env.VERCEL_PROJECT_PRODUCTION_URL);
  }

  if (process.env.VERCEL_BRANCH_URL) {
    domains.add(process.env.VERCEL_BRANCH_URL);
  }

  if (process.env.VERCEL_URL) {
    domains.add(process.env.VERCEL_URL);
  }

  if (process.env.UI_URL) {
    try {
      domains.add(new URL(process.env.UI_URL).host);
    } catch {
      // Invalid URL
    }
  }

  domains.add('localhost:3000');
  domains.add('localhost');

  // Trusted external domains for redirects (e.g., Pylon JWT callback, support portal)
  domains.add('graph.usepylon.com');
  domains.add('support.nominal.so');

  return Array.from(domains);
})();
